import { useEffect, useState } from "react";
import { collection, doc, onSnapshot, orderBy, query } from "firebase/firestore";
import { paths, SalesSettingsSchema, type DocumentSet } from "@clevis/shared-types";
import { Button, Card, CardTitle, ConfirmButton, Input, Label } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { call } from "../lib/callFunction";

// Company documents → Document sets: a named bundle ("Estimate send",
// "Commercial bid") that rides along by default with estimate emails (all
// types or just some), invoice emails and maintenance-agreement emails, and
// is one click in Send documents. The office can still untick anything on a
// given send.

type SetRow = DocumentSet & { id: string };
type Draft = { name: string; docIds: string[]; useFor: { estimates: boolean; invoices: boolean; agreements: boolean }; estimateTypeIds: string[] };
const EMPTY: Draft = { name: "", docIds: [], useFor: { estimates: false, invoices: false, agreements: false }, estimateTypeIds: [] };

export function useDocumentSets(tenantId: string | null | undefined): SetRow[] {
  const [rows, setRows] = useState<SetRow[]>([]);
  useEffect(() => {
    if (!tenantId) return;
    return onSnapshot(
      query(collection(db, paths.tenantDocumentSets(tenantId)), orderBy("name")),
      (snap) => setRows(snap.docs.map((d) => ({ id: d.id, ...(d.data() as DocumentSet) }))),
      () => setRows([]),
    );
  }, [tenantId]);
  return rows;
}

export function DocumentSetsCard({ tenantId, docs, mayEdit }: { tenantId: string; docs: Array<{ id: string; name: string }>; mayEdit: boolean }) {
  const sets = useDocumentSets(tenantId);
  const [types, setTypes] = useState<Array<{ id: string; name: string }>>([]);
  const [editing, setEditing] = useState<{ id: string | null; draft: Draft } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(
    () =>
      onSnapshot(doc(db, paths.tenantSalesSettings(tenantId)), (snap) =>
        setTypes(SalesSettingsSchema.parse(snap.data() ?? {}).estimateTypes.map((t) => ({ id: t.id, name: t.name }))),
      ),
    [tenantId],
  );
  const docName = (id: string) => docs.find((d) => d.id === id)?.name ?? "(removed)";
  const whereText = (s: SetRow) => {
    const parts: string[] = [];
    if (s.useFor.estimates) parts.push(s.estimateTypeIds.length ? `${s.estimateTypeIds.map((id) => types.find((t) => t.id === id)?.name ?? "?").join(", ")} estimates` : "estimate emails");
    if (s.useFor.invoices) parts.push("invoice emails");
    if (s.useFor.agreements) parts.push("agreement emails");
    return parts.length ? `Attached to ${parts.join(", ")}` : "Pick by hand in Send documents";
  };

  async function save() {
    if (!editing) return;
    const d = editing.draft;
    if (!d.name.trim()) return setErr("Give it a name.");
    if (!d.docIds.length) return setErr("Tick at least one document.");
    setBusy(true);
    setErr("");
    try {
      await call("saveDocumentSet", { tenantId, setId: editing.id, set: { ...d, name: d.name.trim() } });
      setEditing(null);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }
  const set = (patch: Partial<Draft>) => setEditing((e) => (e ? { ...e, draft: { ...e.draft, ...patch } } : e));

  return (
    <Card>
      <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
        <CardTitle className="mb-0">Document sets</CardTitle>
        {mayEdit && !editing ? (
          <Button type="button" variant="ghost" className="px-3 py-1 text-xs" onClick={() => setEditing({ id: null, draft: { ...EMPTY, useFor: { ...EMPTY.useFor } } })}>
            New set
          </Button>
        ) : null}
      </div>
      <p className="mb-2 text-xs text-steel">A named bundle that goes out by default: with estimate emails (all types, or just some), invoice emails, agreement emails. Anything can still be unticked on a send.</p>
      {sets.length === 0 && !editing ? <p className="text-xs text-steel">No sets yet. Example: "Estimate send" with your W-9 and the plan brochure, attached to every estimate email.</p> : null}
      <div className="divide-y divide-steel/10 text-sm">
        {sets.map((s) => (
          <div key={s.id} className="flex flex-wrap items-center gap-2 py-1.5">
            <span className="min-w-0 flex-1">
              <span className="font-semibold text-ink">{s.name}</span> <span className="text-xs text-steel">· {s.docIds.map(docName).join(", ")}</span>
              <div className="text-xs text-steel">{whereText(s)}</div>
            </span>
            {mayEdit ? (
              <span className="flex items-center gap-1">
                <Button type="button" variant="ghost" className="px-2 py-1 text-xs" onClick={() => setEditing({ id: s.id, draft: { name: s.name, docIds: [...s.docIds], useFor: { ...s.useFor }, estimateTypeIds: [...s.estimateTypeIds] } })}>
                  Edit
                </Button>
                <ConfirmButton variant="ghost" className="px-2 py-1 text-xs text-steel" confirm="Delete this set? The documents stay in the library." confirmLabel="Delete" onConfirm={() => call("deleteDocumentSet", { tenantId, setId: s.id })}>
                  Delete
                </ConfirmButton>
              </span>
            ) : null}
          </div>
        ))}
      </div>
      {editing ? (
        <div className="mt-3 space-y-3 rounded-lg border border-steel/15 p-3">
          <div>
            <Label>Name</Label>
            <Input value={editing.draft.name} maxLength={80} placeholder="Estimate send" onChange={(e) => set({ name: e.target.value })} />
          </div>
          <div>
            <Label>Documents</Label>
            {docs.length === 0 ? <p className="text-xs text-steel">Add documents to the library first.</p> : null}
            <div className="grid gap-1 sm:grid-cols-2">
              {docs.map((d) => (
                <label key={d.id} className="flex cursor-pointer items-center gap-2 text-sm text-ink">
                  <input
                    type="checkbox"
                    checked={editing.draft.docIds.includes(d.id)}
                    onChange={(e) => set({ docIds: e.target.checked ? [...editing.draft.docIds, d.id] : editing.draft.docIds.filter((x) => x !== d.id) })}
                  />
                  {d.name}
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label>Goes out by default with</Label>
            <div className="flex flex-wrap gap-4 text-sm">
              {(["estimates", "invoices", "agreements"] as const).map((k) => (
                <label key={k} className="flex cursor-pointer items-center gap-2 text-ink">
                  <input type="checkbox" checked={editing.draft.useFor[k]} onChange={(e) => set({ useFor: { ...editing.draft.useFor, [k]: e.target.checked } })} />
                  {k === "estimates" ? "Estimate emails" : k === "invoices" ? "Invoice emails" : "Maintenance-agreement emails"}
                </label>
              ))}
            </div>
            {editing.draft.useFor.estimates && types.length ? (
              <div className="mt-1 flex flex-wrap items-center gap-3 text-sm">
                <span className="text-xs text-steel">Estimate types (none ticked = all):</span>
                {types.map((t) => (
                  <label key={t.id} className="flex cursor-pointer items-center gap-1.5 text-ink">
                    <input
                      type="checkbox"
                      checked={editing.draft.estimateTypeIds.includes(t.id)}
                      onChange={(e) => set({ estimateTypeIds: e.target.checked ? [...editing.draft.estimateTypeIds, t.id] : editing.draft.estimateTypeIds.filter((x) => x !== t.id) })}
                    />
                    {t.name}
                  </label>
                ))}
              </div>
            ) : null}
          </div>
          {err ? <p className="text-xs text-danger">{err}</p> : null}
          <div className="flex items-center gap-2">
            <Button type="button" busy={busy} onClick={() => void save()}>
              Save
            </Button>
            <Button type="button" variant="ghost" onClick={() => setEditing(null)}>
              Cancel
            </Button>
          </div>
        </div>
      ) : null}
    </Card>
  );
}
